/*
 * Setzt frisches Section-Markup ein, ohne den vorhandenen DOM wegzuwerfen.
 *
 * `innerHTML = ...` hat bei jeder Mengenaenderung den halben Drawer neu
 * gebaut: graue Bild-Platzhalter, Einblend-Animationen von vorne,
 * Scrollposition und Fokus weg. theme.morph gleicht stattdessen an, siehe
 * assets/dom-morph.js. Faellt das Skript aus, bleibt der alte Weg.
 */
function renderSection(element, html) {
  if (element == null || html == null) return;

  if (window.theme && typeof theme.morph === 'function') {
    theme.morph(element, html);
  } else {
    element.innerHTML = html;
  }
}

/*
 * Ladezustand des Checkout-Buttons.
 *
 * Solange der Warenkorb rechnet — Menge geaendert, Zeile entfernt, Geschenk
 * kommt dazu oder faellt weg — steht die Summe daneben noch auf dem alten
 * Stand. Der Spinner sagt "warte kurz", ohne dass der Button gesperrt wird:
 * wer trotzdem klickt, kommt in den Checkout, Shopify rechnet dort ohnehin
 * frisch. `.button.loading` ist die Theme-eigene Animation aus base.css.
 *
 * Ein Zaehler statt an/aus: nach einer Mengenaenderung legt
 * assets/cart-milestone-picker.js das Geschenk erst in eigenen Requests nach.
 * Mit einem einfachen Schalter ging der Spinner aus, sobald die Menge durch
 * war, und der Warenkorb stand ein paar Sekunden ohne Geschenk und ohne
 * Spinner da. Der Picker meldet sich ueber theme.setCheckoutLoading mit an,
 * und zwar schon beim Einsetzen, also bevor cart.js hier abmeldet.
 */
let checkoutBusy = 0;

function setCheckoutLoading(isLoading) {
  checkoutBusy = Math.max(0, checkoutBusy + (isLoading ? 1 : -1));
  document.querySelectorAll('[name="checkout"]').forEach((button) => {
    button.classList.toggle('loading', checkoutBusy > 0);
  });
}

window.theme = window.theme || {};
theme.setCheckoutLoading = setCheckoutLoading;

/*
 * Nach dem Klick laeuft der Spinner weiter, bis der Checkout da ist — sonst
 * sieht die Sekunde bis zum Seitenwechsel aus, als waere nichts passiert, und
 * es wird ein zweites Mal geklickt. Kommt der Kunde ueber den Zurueck-Button
 * wieder (bfcache), ist der Button sonst fuer immer im Ladezustand.
 */
document.addEventListener('click', (event) => {
  // Mit Cmd/Strg/Shift oder von einer App abgefangen folgt kein Seitenwechsel,
  // der Spinner liefe dann bis zum naechsten Nachrendern.
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
  const button = event.target.closest('[name="checkout"]');
  if (button) button.classList.add('loading');
});

window.addEventListener('pageshow', () => {
  checkoutBusy = 0;
  document.querySelectorAll('[name="checkout"]').forEach((button) => button.classList.remove('loading'));
});

/*
 * Warenkorb-Zeilen werden ueber ihren Line-Key angesprochen, nicht ueber die
 * Positionsnummer. Die Nummer verschiebt sich, sobald das Geschenk dazukommt
 * oder wegfaellt — und das passiert in eigenen Requests, zeitlich zwischen
 * zwei Taps. Ein "+" auf Zeile 2 traf dann das Produkt darunter, oder eine
 * Zeile, die es nicht mehr gab (422). Markup: data-line-key in
 * sections/mini-cart.liquid und sections/main-cart-items.liquid.
 */
function lineKeyOf(element) {
  const row = element && element.closest ? element.closest('[data-line-key]') : null;
  return row ? row.dataset.lineKey : null;
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.closest('cart-items').updateQuantity(this.dataset.index, 0, null, lineKeyOf(this));
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.cartErrors = document.getElementById('cart-errors');

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    /*
     * Ein Mengenfeld gehoert dem Kunden, solange er getippt hat und das noch
     * nicht rausging (Debounce) oder solange noch ein Request dafuer laeuft.
     * Solange darf das Nachrendern — die Antwort auf eine fruehere Aenderung,
     * ein Geschenk, das der Picker nachlegt — die Zahl nicht zuruecksetzen,
     * sonst rechnete der naechste Tap vom alten Wert aus weiter. Erst die
     * Antwort auf den letzten Request setzt das Feld wieder, dann auch auf den
     * Bestand, wenn zu viel gewuenscht war. Siehe morphKeepValue in
     * assets/dom-morph.js.
     */
    this.unsentInputs = new WeakSet();
    this.inFlightInputs = new Map();

    this.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      this.unsentInputs.add(event.target);
      this.syncKeepValue(event.target);
    });
    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  syncKeepValue(input) {
    if (!input || !window.theme || !theme.morphKeepValue) return;
    if (this.unsentInputs.has(input) || this.inFlightInputs.has(input)) {
      theme.morphKeepValue.add(input);
    } else {
      theme.morphKeepValue.delete(input);
    }
  }

  trackInFlight(input, delta) {
    if (!input) return;
    const count = (this.inFlightInputs.get(input) || 0) + delta;
    if (count > 0) {
      this.inFlightInputs.set(input, count);
    } else {
      this.inFlightInputs.delete(input);
    }
    this.syncKeepValue(input);
  }

  onChange(event) {
    if (event.target === null) return;
    this.unsentInputs.delete(event.target);
    const activeName = document.activeElement ? document.activeElement.getAttribute('name') : null;
    this.updateQuantity(event.target.dataset.index, event.target.value, activeName, lineKeyOf(event.target));
  }

  findLine(key) {
    if (!key) return null;
    return Array.from(this.querySelectorAll('[data-line-key]')).find((row) => row.dataset.lineKey === key) || null;
  }

  getSectionsToRender() {
    let sections = [
      {
        id: 'mini-cart',
        section: document.getElementById('mini-cart')?.id,
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      }
    ];
    if (document.querySelector('#main-cart-footer .free-shipping')) {
      sections.push({
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.free-shipping',
      });
    }
    return sections;
  }

  updateQuantity(line, quantity, name, key = null) {
    this.enableLoading(line, key);

    const row = this.findLine(key);
    const input = row ? row.querySelector('input[name="updates[]"]') : null;
    this.trackInFlight(input, 1);
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      this.trackInFlight(input, -1);
    };
    // Doppelte raus: auf /cart steht main-cart-footer zweimal in der Liste,
    // und mehr als fuenf Sections nimmt /cart/change.js nicht an.
    const sections = [...new Set(this.getSectionsToRender().map((section) => section.section).filter(Boolean))];

    const body = JSON.stringify({
      ...(key ? { id: key } : { line }),
      quantity,
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(`${theme.routes.cart_change_url}`, {...fetchConfig(), ...{ body }})
      .then((response) => response.text().then((state) => ({ ok: response.ok, state })))
      .then(({ ok, state }) => {
        settle();
        const parsedState = JSON.parse(state);

        // Reicht die Wunschmenge ueber den Bestand, lehnt /cart/change.js mit
        // 422 ab. Der Text kommt dabei mal als `errors`, mal als `description`,
        // und ein `status` im Rumpf gibt es auch nicht immer — deshalb zaehlt
        // hier der HTTP-Status und nicht, was im JSON steht. `sections` fehlt
        // in jedem dieser Faelle: frueher lief der Durchlauf unten trotzdem
        // darueber und warf einen TypeError, danach blieb das Mengenfeld auf
        // der abgelehnten Zahl stehen. Jetzt wird die Zeile frisch geholt,
        // damit die angezeigte Menge wieder zum Warenkorb passt, und die
        // Meldung kommt danach — das Nachrendern wuerde sie sonst ueberschreiben.
        if (!ok || parsedState.status === 422 || parsedState.status === 404) {
          const message = parsedState.errors || parsedState.description || parsedState.message;
          this.refreshFromCart().then(() => this.updateErrorLiveRegions(line, message, key));
          this.disableLoading();
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        this.getSectionsToRender().forEach((section => {
          const element = document.getElementById(section.id);
          if (element && parsedState.sections) {
            const elementToReplace = element.querySelector(section.selector) || element;

            if (elementToReplace && parsedState.sections[section.section]) {
              renderSection(
                elementToReplace,
                this.getSectionInnerHTML(parsedState.sections[section.section], section.selector)
              );
            }
          }
        }));
        
        // Teilweise angenommen (200 mit `errors`): die Meldung erst jetzt, nach
        // dem Nachrendern — davor haette sie das Rendern gleich wieder geleert.
        if (parsedState.errors) {
          this.updateErrorLiveRegions(line, parsedState.errors, key);
        }

        this.updateQuantityLiveRegions(key, quantity, parsedState);

        const lineItem = this.findLine(key) || document.getElementById(`CartItem-${line}`);
        const focusTarget = lineItem && name ? lineItem.querySelector(`[name="${name}"]`) : null;
        // Ohne preventScroll scrollt iOS den Button beim Fokussieren in die
        // Mitte, der Drawer sprang dadurch bei jedem Tap nach unten.
        if (focusTarget) focusTarget.focus({ preventScroll: true });
        this.disableLoading();

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: state
          }
        }));
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      })
      .catch(() => {
        settle();
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        this.disableLoading();
        if (this.cartErrors) {
          this.cartErrors.textContent = theme.cartStrings.error;
        }
      });
  }
  
  // Holt die betroffenen Sections neu, ohne etwas am Warenkorb zu aendern.
  // Gebraucht nach einer 422: die Antwort enthaelt keine Sections, das Feld im
  // Stepper steht aber noch auf der abgelehnten Wunschmenge.
  refreshFromCart() {
    const sections = this.getSectionsToRender().filter((section) => section.section);
    if (!sections.length) return Promise.resolve();

    const sectionIds = [...new Set(sections.map((section) => section.section))];
    const url = `${window.location.pathname}?sections=${sectionIds.join(',')}`;

    return fetch(url)
      .then((response) => response.json())
      .then((parsedSections) => {
        sections.forEach((section) => {
          const element = document.getElementById(section.id);
          if (!element || !parsedSections[section.section]) return;

          const elementToReplace = element.querySelector(section.selector) || element;
          renderSection(
            elementToReplace,
            this.getSectionInnerHTML(parsedSections[section.section], section.selector)
          );
        });
      })
      .catch(() => {});
  }

  lineItemErrorElement(line, key) {
    const row = this.findLine(key);
    if (row) return row.querySelector('.cart-item__error');
    return document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
  }

  updateErrorLiveRegions(line, message, key = null) {
    const lineItemError = this.lineItemErrorElement(line, key);
    const errorText = lineItemError ? lineItemError.querySelector('.cart-item__error-text') : null;
    if (errorText && message) errorText.innerHTML = message;
  
    // Beide Elemente stehen nur in sections/main-cart-items.liquid, also auf der
    // Warenkorb-Seite. Im Drawer warfen sie hier einen TypeError, der das
    // ganze .then() abbrach: das Nachrendern der Zeilen fiel aus und der
    // Mengen-Stepper sah aus, als reagiere er nicht (Clarity, tote Taps).
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;

    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }
  
  /*
   * "Du kannst nur X davon in den Warenkorb legen", wenn der Warenkorb weniger
   * angenommen hat als gewuenscht. Frueher wurde dafuer die Gesamtstueckzahl
   * vor und nach der Aenderung verglichen. Der Vergleichswert kam aus dem
   * Konstruktor, und seit das Nachrendern <cart-items> stehen laesst
   * (assets/dom-morph.js), lief er nach jedem Hinzufuegen oder Entfernen
   * ausserhalb dieser Klasse falsch — die Meldung stand dann an Zeilen, an
   * denen gar nichts abgelehnt worden war. Jetzt zaehlt die Zeile selbst.
   */
  updateQuantityLiveRegions(key, requested, parsedState) {
    const item = key && Array.isArray(parsedState.items)
      ? parsedState.items.find((lineItem) => lineItem.key === key)
      : null;
    const quantityError = item ? this.lineItemErrorElement(null, key) : null;
    const errorText = quantityError ? quantityError.querySelector('.cart-item__error-text') : null;

    if (errorText && parseInt(requested, 10) > item.quantity) {
      errorText.innerHTML = theme.cartStrings.quantityError.replace('[quantity]', item.quantity);
    }

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1e3);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML;
  }

  enableLoading(line, key = null) {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) cartItems.classList.add('cart__items--disabled');

    setCheckoutLoading(true);

    const row = this.findLine(key);
    const loadingOverlay = row
      ? row.querySelector('.loading-overlay')
      : this.querySelectorAll('.loading-overlay')[line - 1];
    if (loadingOverlay) loadingOverlay.classList.remove('hidden');
    
    if (document.activeElement) document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) cartItems.classList.remove('cart__items--disabled');

    setCheckoutLoading(false);
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section => {
      const element = document.getElementById(section.id);

      if (element) {
        renderSection(element, this.getSectionInnerHTML(parsedState.sections[section.id], section.selector));
      }
    }));
  }
}
customElements.define('cart-items', CartItems);

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', debounce((event) => {
      const body = JSON.stringify({ note: event.target.value });
      fetch(`${theme.routes.cart_update_url}`, {...fetchConfig(), ...{ body }});
    }, 300));
  }
}
customElements.define('cart-note', CartNote);

class DiscountCode extends HTMLElement {
  constructor() {
    super();

    if (isStorageSupported('session')) {
      this.setupDiscount();

      this.addEventListener('change', (event) => {
        window.sessionStorage.setItem('discount', event.target.value);
      });
    }
  }

  setupDiscount() {
    const discount = window.sessionStorage.getItem('discount');
    if (discount !== null) {
      this.querySelector('input[name="discount"]').value = discount;
    }
  }
}

customElements.define('discount-code', DiscountCode);

class ShippingCalculator extends HTMLElement {
  constructor() {
    super();

    this.setupCountries();
    
    this.errors = this.querySelector('#ShippingCalculatorErrors');
    this.success = this.querySelector('#ShippingCalculatorSuccess');
    this.zip = this.querySelector('#ShippingCalculatorZip');
    this.country = this.querySelector('#ShippingCalculatorCountry');
    this.province = this.querySelector('#ShippingCalculatorProvince');
    this.button = this.querySelector('button');
    this.button.addEventListener('click', this.onSubmitHandler.bind(this));
  }

  setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector) {
      // eslint-disable-next-line no-new
      new Shopify.CountryProvinceSelector('ShippingCalculatorCountry', 'ShippingCalculatorProvince', {
        hideElement: 'ShippingCalculatorProvinceContainer'
      });
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();
    
    this.errors.classList.add('hidden');
    this.success.classList.add('hidden');
    this.zip.classList.remove('invalid');
    this.country.classList.remove('invalid');
    this.province.classList.remove('invalid');
    this.button.classList.add('loading');
    this.button.setAttribute('disabled', true);

    const body = JSON.stringify({
      shipping_address: {
        zip: this.zip.value,
        country: this.country.value,
        province: this.province.value
      }
    });
    let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

    // remove double `/` in case shop might have /en or language in URL
    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.success.classList.remove('hidden');
          this.success.innerHTML = '';
          
          parsedState.shipping_rates.forEach((rate) => {
            const child = document.createElement('p');
            child.innerHTML = `${rate.name}: ${rate.price} ${Shopify.currency.active}`;
            this.success.appendChild(child);
          });
        }
        else {
          let errors = [];
          Object.entries(parsedState).forEach(([attribute, messages]) => {
            errors.push(`${attribute.charAt(0).toUpperCase() + attribute.slice(1)} ${messages[0]}`);
          });

          this.errors.classList.remove('hidden');
          this.errors.querySelector('.errors').innerHTML = errors.join('; ');
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.button.classList.remove('loading');
        this.button.removeAttribute('disabled');
      });
  }
}

customElements.define('shipping-calculator', ShippingCalculator);
